import { PageTypeV2, v4 } from '@sap/ux-specification-types';

const PAGE_TYPE_TO_CONTROLLER_EXTENSION_MAP = new Map<PageTypeV2, string>([
    [PageTypeV2.ListReport, v4.SAPUI5_VIEW_EXTENSION_LIST_REPORT],
    [PageTypeV2.ObjectPage, v4.SAPUI5_VIEW_EXTENSION_OBJECT_PAGE]
]);

/**
 * Method transfers values of 'controllerNames' and 'controllerName' from source to target.
 * Method can be uses in both ways - import or export.
 *
 * @param source - source object
 * @param target - target object
 */
const applyControllerNameProperties = (
    source: v4.SapUi5ControllerExtensionV4,
    target: v4.SapUi5ControllerExtensionV4
) => {
    const properties = ['controllerNames', 'controllerName'];
    for (const property of properties) {
        if (property in source) {
            target[property] = source[property];
        } else if (property in target) {
            // Deletion of property happens
            delete target[property];
        }
    }
};

/**
 * Method returns extension key for import(page.json).
 *
 * @param {string} manifestKey - extension name in manifest.
 * @returns - extension key for import.
 */
const getExtensionKeyForImport = (manifestKey: string): string | undefined => {
    for (const [pageType, key] of PAGE_TYPE_TO_CONTROLLER_EXTENSION_MAP) {
        if (manifestKey?.startsWith(key)) {
            // Replace SAPUI5 specific key like 'sap.fe.templates.ObjectPage.ObjectPageController' with more readable PageTypeV2
            let configKey: string = pageType;
            if (manifestKey.includes('#')) {
                // Append with page id
                const id = manifestKey.split('#')[1]?.split('::')[1];
                configKey += `#${id}`;
            }
            return configKey;
        }
    }
    return undefined;
};

/**
 * Method returns extension key for export(manifest.json).
 *
 * @param {string} configKey - extension name in config.
 * @param {string} appId - application id.
 * @returns - extension key for export.
 */
const getExtensionKeyForExport = (configKey: string, appId: string): string => {
    let manifestKey = '';
    for (const [pageType, key] of PAGE_TYPE_TO_CONTROLLER_EXTENSION_MAP) {
        if (configKey.startsWith(pageType)) {
            manifestKey = key;
        }
    }
    const idParts = configKey.split('#');
    if (idParts.length > 1) {
        const pageId = idParts[1];
        manifestKey = `${manifestKey}#${appId}::${pageId}`;
    }
    return manifestKey;
};

export const controllerExtensions = {
    manifest: {
        path: (): string => `sap.ui5/extends/extensions`,
        key: v4.SAPUI5_CONTROLLER_EXTENSION,
        import: (extensions: v4.SapUi5ControllerExtensionsV4): v4.ControllerExtensions | undefined => {
            const result: v4.ControllerExtensions = {};
            const controllerExtensions =
                extensions && typeof extensions === 'object' && v4.SAPUI5_CONTROLLER_EXTENSION in extensions
                    ? extensions[v4.SAPUI5_CONTROLLER_EXTENSION]
                    : {};
            if (controllerExtensions && typeof controllerExtensions === 'object') {
                for (const manifestKey in controllerExtensions) {
                    const configKey = getExtensionKeyForImport(manifestKey);
                    if (configKey) {
                        result[configKey] = {};
                        applyControllerNameProperties(controllerExtensions[manifestKey], result[configKey]);
                    }
                }
            }
            return Object.keys(result).length ? result : undefined;
        },
        export: (
            manifestSection: v4.SapUi5ControllerExtensionsV4,
            configObject: v4.ApplicationV4,
            key: string
        ): void => {
            const appId = configObject?.id || '';
            const configExtensions = configObject?.settings?.[key] || {};
            if (!(v4.SAPUI5_CONTROLLER_EXTENSION in manifestSection) && Object.keys(configExtensions).length) {
                manifestSection[v4.SAPUI5_CONTROLLER_EXTENSION] = {};
            }
            const controllerExtensions = manifestSection ? manifestSection[v4.SAPUI5_CONTROLLER_EXTENSION] : {};
            // Go through all config extensions
            for (const configKey in configExtensions) {
                const manifestKey = getExtensionKeyForExport(configKey, appId);
                if (!controllerExtensions[manifestKey]) {
                    controllerExtensions[manifestKey] = {};
                }
                applyControllerNameProperties(configExtensions[configKey], controllerExtensions[manifestKey]);
            }
            // Handle removed extensions
            for (const manifestKey in controllerExtensions) {
                const configKey = getExtensionKeyForImport(manifestKey);
                if (configKey && !configExtensions[configKey]) {
                    delete controllerExtensions[manifestKey];
                }
            }
        }
    }
};
